import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Briefcase, MapPin, Upload, FileText, X, Loader2,
    CheckCircle2, AlertCircle, ArrowRight, ShieldCheck, Clock, Sparkles,
    Building2, Users, Calendar, Globe, Linkedin, Lock, ExternalLink, Star,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { API_CONFIG } from '@/config/config.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * PublicJobPage — the candidate-facing job posting reached via a shared link.
 *
 * Rendered outside the authenticated app shell: no sidebar, no login required.
 * The audience is every kind of applicant, so the page stays to a single column,
 * plain language, and one obvious action.
 */

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXT   = ['.pdf', '.docx', '.doc', '.txt'];
const EMAIL_RE       = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatPostedDate(value) {
    if (!value) return '';
    const days = Math.floor((Date.now() - new Date(value)) / 86400000);
    if (days <= 0) return 'Posted today';
    if (days === 1) return 'Posted yesterday';
    if (days < 30) return `Posted ${days} days ago`;
    const months = Math.floor(days / 30);
    return `Posted ${months} month${months > 1 ? 's' : ''} ago`;
}

/**
 * Job descriptions are stored as free text. Render them as readable blocks,
 * promoting bullet-like lines to a real list so pasted JDs do not appear as
 * one dense paragraph.
 */
function JobDescription({ text }) {
    const blocks = [];
    let bullets = [];

    const flushBullets = () => {
        if (!bullets.length) return;
        blocks.push(
            <ul key={`ul-${blocks.length}`} className="space-y-2 pl-1">
                {bullets.map((item, i) => (
                    <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-muted-foreground">
                        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/50" />
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        );
        bullets = [];
    };

    for (const rawLine of (text || '').split('\n')) {
        const line = rawLine.trim();
        if (!line) { flushBullets(); continue; }

        const bulletMatch = line.match(/^[-•*▪·]\s*(.+)/);
        if (bulletMatch) { bullets.push(bulletMatch[1]); continue; }

        flushBullets();

        // A short line ending in a colon reads as a section heading.
        const isHeading = line.length < 80 && (line.endsWith(':') || /^[A-Z][A-Za-z\s&/]{2,40}$/.test(line));
        blocks.push(
            isHeading
                ? <h3 key={blocks.length} className="pt-2 text-sm font-semibold tracking-tight text-foreground">
                    {line.replace(/:$/, '')}
                  </h3>
                : <p key={blocks.length} className="text-[15px] leading-relaxed text-muted-foreground">{line}</p>
        );
    }
    flushBullets();

    return <div className="space-y-3">{blocks}</div>;
}

/* ── Page states ─────────────────────────────────────────────────────────── */

function CenteredMessage({ icon: Icon, tone = 'muted', title, children }) {
    const tones = {
        muted:   'bg-muted text-muted-foreground',
        danger:  'bg-destructive/10 text-destructive',
        success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    };
    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4 py-16">
            <div className="w-full max-w-md text-center">
                <div className={cn('mx-auto flex h-16 w-16 items-center justify-center rounded-2xl', tones[tone])}>
                    <Icon className="h-7 w-7" />
                </div>
                <h1 className="mt-6 text-xl font-semibold tracking-tight text-foreground">{title}</h1>
                <div className="mt-2 text-sm leading-relaxed text-muted-foreground">{children}</div>
            </div>
        </div>
    );
}

function PageSkeleton() {
    return (
        <div className="min-h-screen bg-background">
            <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
                <div className="animate-pulse space-y-6">
                    <div className="h-12 w-12 rounded-xl bg-muted" />
                    <div className="h-8 w-2/3 rounded-lg bg-muted" />
                    <div className="h-4 w-1/3 rounded bg-muted" />
                    <div className="space-y-3 pt-6">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="h-4 rounded bg-muted" style={{ width: `${70 + (i % 3) * 10}%` }} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Resume picker ───────────────────────────────────────────────────────── */

function ResumeField({ file, onPick, onClear, error, disabled }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        if (disabled) return;
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) onPick(dropped);
    };

    return (
        <div className="space-y-1.5">
            <Label htmlFor="resume-input">
                Resume <span className="text-destructive">*</span>
            </Label>

            {file ? (
                <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                        <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClear}
                        disabled={disabled}
                        aria-label="Remove selected resume"
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => !disabled && inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    disabled={disabled}
                    className={cn(
                        'flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-8 transition-colors',
                        dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted/40',
                        error && 'border-destructive/50',
                        disabled && 'cursor-not-allowed opacity-60',
                    )}
                >
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
                        <Upload className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-foreground">
                        Click to upload <span className="font-normal text-muted-foreground">or drag and drop</span>
                    </p>
                    <p className="text-xs text-muted-foreground">PDF, DOCX, DOC or TXT · up to 10 MB</p>
                </button>
            )}

            <input
                ref={inputRef}
                id="resume-input"
                type="file"
                accept={ACCEPTED_EXT.join(',')}
                className="sr-only"
                onChange={(e) => {
                    const picked = e.target.files?.[0];
                    if (picked) onPick(picked);
                    e.target.value = '';   // allow re-picking the same file
                }}
            />
            {error && <p className="text-xs font-medium text-destructive">{error}</p>}
        </div>
    );
}

/* ── Main page ───────────────────────────────────────────────────────────── */


/**
 * Shown once a company stops hiring for a role.
 *
 * Keeps the company's identity and the role title so a saved link still reads
 * as a real posting that closed, not a broken URL — but the requirements are
 * gone, because the server no longer sends them once hiring ends.
 */
function ClosedPosting({ job }) {
    const c = job.company || {};
    const monogram = (c.name || '')
        .split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
    const logo = c.logoUrl
        ? (c.logoUrl.startsWith('http') ? c.logoUrl : `${API_CONFIG.STATIC_BASE_URL}${c.logoUrl}`)
        : null;

    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4 py-16">
            <div className="w-full max-w-md">
                <div className="rounded-2xl border border-border bg-card p-6 text-center sm:p-8">
                    {logo ? (
                        <img src={logo} alt={c.name}
                            className="mx-auto h-14 w-14 rounded-xl border border-border bg-background object-contain p-1.5" />
                    ) : (
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-base font-bold text-primary ring-1 ring-primary/15">
                            {monogram}
                        </div>
                    )}

                    <p className="mt-4 text-sm font-medium text-muted-foreground">{c.name}</p>
                    <h1 className="mt-1 text-xl font-semibold leading-tight tracking-tight text-foreground">
                        {job.title}
                    </h1>
                    {(job.department || job.position) && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            {[job.department, job.position].filter(Boolean).join(' · ')}
                        </p>
                    )}

                    <div className="mt-6 rounded-xl border border-border bg-muted/40 px-4 py-3.5">
                        <p className="flex items-center justify-center gap-1.5 text-sm font-medium text-foreground">
                            <Lock className="h-3.5 w-3.5" />
                            No longer accepting applications
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            This role has closed. Thank you for your interest.
                        </p>
                    </div>

                    {c.website && (
                        <Button asChild variant="outline" className="mt-5 w-full gap-1.5">
                            <a href={c.website} target="_blank" rel="noopener noreferrer nofollow">
                                See other openings at {c.name}
                                <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                        </Button>
                    )}
                </div>

                <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3" />Powered by EchoHire
                </p>
            </div>
        </div>
    );
}


/**
 * Skills checklist on the apply form.
 *
 * Optional by design — a candidate who skips it still applies successfully,
 * they just carry no skill signal. Ticking nothing is never a barrier.
 */
function SkillsChecklist({ skills, selected, onToggle, disabled }) {
    if (!skills?.length) return null;
    const requiredCount = skills.filter(s => s.required).length;

    return (
        <div className="space-y-2">
            <Label>
                Which of these do you have?{' '}
                <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <p className="-mt-1 text-[11px] leading-relaxed text-muted-foreground">
                Tick everything that applies. It helps the hiring team review your
                application faster{requiredCount > 0 ? ' — starred skills matter most for this role' : ''}.
            </p>

            <div className="grid gap-1.5 rounded-xl border border-border p-2.5 sm:grid-cols-2">
                {skills.map(s => {
                    const on = selected.includes(s.name);
                    return (
                        <label
                            key={s.name}
                            className={cn(
                                'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition-colors',
                                on ? 'bg-primary/10' : 'hover:bg-muted/60',
                                disabled && 'cursor-not-allowed opacity-60',
                            )}
                        >
                            <input
                                type="checkbox"
                                checked={on}
                                onChange={() => onToggle(s.name)}
                                disabled={disabled}
                                className="h-4 w-4 shrink-0 rounded border-input"
                            />
                            <span className={cn('min-w-0 flex-1 truncate text-sm', on ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                                {s.name}
                            </span>
                            {s.required && (
                                <Star className="h-3 w-3 shrink-0 text-amber-500" aria-label="Most important" />
                            )}
                        </label>
                    );
                })}
            </div>

            {selected.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                    {selected.length} of {skills.length} selected
                </p>
            )}
        </div>
    );
}

export default function PublicJobPage() {
    const { slug } = useParams();

    const [job,       setJob]       = useState(null);
    const [loadError, setLoadError] = useState('');
    const [loading,   setLoading]   = useState(true);

    const [form,      setForm]      = useState({ fullName: '', email: '', phone: '' });
    const [file,      setFile]      = useState(null);
    const [skills,    setSkills]    = useState([]);   // ticked skill names
    const [errors,    setErrors]    = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitted, setSubmitted] = useState(null);   // { alreadyApplied } | { jobTitle }

    const formRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiClient.getPublicJob(slug);
                if (!cancelled) setJob(res.data);
            } catch (err) {
                if (!cancelled) setLoadError(err.message || 'This job posting is no longer available.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    // Keep the browser tab meaningful when the link is shared around.
    useEffect(() => {
        if (job) document.title = `${job.title} · ${job.company.name}`;
    }, [job]);

    const setField = (key) => (e) => {
        setForm(prev => ({ ...prev, [key]: e.target.value }));
        setErrors(prev => ({ ...prev, [key]: undefined }));
        setSubmitError('');
    };

    const pickFile = useCallback((picked) => {
        const ext = picked.name.slice(picked.name.lastIndexOf('.')).toLowerCase();
        if (!ACCEPTED_EXT.includes(ext)) {
            setErrors(prev => ({ ...prev, file: 'Please upload a PDF, DOCX, DOC or TXT file.' }));
            return;
        }
        if (picked.size > MAX_FILE_BYTES) {
            setErrors(prev => ({ ...prev, file: 'That file is larger than 10 MB. Please upload a smaller one.' }));
            return;
        }
        setFile(picked);
        setErrors(prev => ({ ...prev, file: undefined }));
        setSubmitError('');
    }, []);

    const validate = () => {
        const next = {};
        if (!form.fullName.trim())            next.fullName = 'Please enter your full name.';
        else if (form.fullName.trim().length < 2) next.fullName = 'Please enter your full name.';
        if (!form.email.trim())               next.email = 'Please enter your email address.';
        else if (!EMAIL_RE.test(form.email.trim())) next.email = 'Please enter a valid email address.';
        if (!file)                            next.file = 'Please attach your resume.';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitError('');
        if (!validate()) return;

        setSubmitting(true);
        try {
            const res = await apiClient.applyToPublicJob(slug, {
                fullName: form.fullName.trim(),
                email:    form.email.trim(),
                phone:    form.phone.trim(),
                resume:   file,
                declaredSkills: skills,
            });
            setSubmitted({ jobTitle: res.data?.jobTitle || job.title });
        } catch (err) {
            if (err.code === 'ALREADY_APPLIED') {
                setSubmitted({ alreadyApplied: true, jobTitle: job.title });
            } else if (err.code === 'APPLICATIONS_CLOSED') {
                // Closed while this form was open — switch the whole page over.
                setJob(prev => ({ ...prev, applicationsClosed: true }));
            } else {
                setSubmitError(err.message || 'Could not submit your application. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading)   return <PageSkeleton />;
    if (loadError) {
        return (
            <CenteredMessage icon={AlertCircle} tone="muted" title="This job is no longer accepting applications">
                The link may have expired or the position has been filled.
                Please check with the company for current openings.
            </CenteredMessage>
        );
    }

    // A closed role never reaches the form, so this sits ahead of it.
    if (job?.applicationsClosed) return <ClosedPosting job={job} />;

    // ── Confirmation ────────────────────────────────────────────────────────
    if (submitted) {
        const already = submitted.alreadyApplied;
        return (
            <CenteredMessage
                icon={already ? CheckCircle2 : CheckCircle2}
                tone={already ? 'muted' : 'success'}
                title={already ? 'You have already applied' : 'Application submitted'}
            >
                {already ? (
                    <>
                        We already have your application for <strong className="text-foreground">{submitted.jobTitle}</strong>.
                        There is no need to apply again — the hiring team will reach out if your profile matches.
                    </>
                ) : (
                    <>
                        Thanks, {form.fullName.trim().split(/\s+/)[0]}. Your application for{' '}
                        <strong className="text-foreground">{submitted.jobTitle}</strong> has been received.
                        <span className="mt-3 block">
                            The hiring team will review your resume and contact you at{' '}
                            <strong className="text-foreground">{form.email.trim()}</strong> if there is a match.
                        </span>
                    </>
                )}
            </CenteredMessage>
        );
    }

    const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Falls back to a monogram when the company has not uploaded a logo.
    const monogram = job.company.name
        .split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
    const logoSrc = job.company.logoUrl
        ? (job.company.logoUrl.startsWith('http')
            ? job.company.logoUrl
            : `${API_CONFIG.STATIC_BASE_URL}${job.company.logoUrl}`)
        : null;

    const c = job.company;
    // Only render the About section when the company actually filled something in.
    const companyFacts = [
        c.industry    && { icon: Building2, label: c.industry },
        c.companySize && { icon: Users,     label: `${c.companySize} employees` },
        c.location    && { icon: MapPin,    label: c.location },
        c.foundedYear && { icon: Calendar,  label: `Founded ${c.foundedYear}` },
    ].filter(Boolean);
    const hasAboutSection = Boolean(c.about) || companyFacts.length > 0 || Boolean(c.website);

    return (
        <div className="min-h-screen bg-background">
            {/* ── Hero ───────────────────────────────────────────────────── */}
            <header className="border-b border-border bg-gradient-to-b from-muted/40 to-background">
                <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
                    {/* Company identity — an applicant must know who is hiring
                        before anything else on the page. */}
                    <div className="flex items-center gap-3">
                        {logoSrc ? (
                            <img src={logoSrc} alt={job.company.name}
                                className="h-11 w-11 shrink-0 rounded-xl border border-border bg-card object-contain p-1" />
                        ) : (
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold tracking-tight text-primary ring-1 ring-primary/15">
                                {monogram}
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="truncate text-base font-semibold tracking-tight text-foreground">
                                {job.company.name}
                            </p>
                            <p className="text-xs text-muted-foreground">is hiring</p>
                        </div>
                    </div>

                    <h1 className="mt-5 text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
                        {job.title}
                    </h1>

                    {/* Meta chips */}
                    <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                        {job.department && (
                            <span className="flex items-center gap-1.5">
                                <Briefcase className="h-3.5 w-3.5" />{job.department}
                            </span>
                        )}
                        {job.position && (
                            <span className="flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5" />{job.position}
                            </span>
                        )}
                        {job.postedAt && (
                            <span className="flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5" />{formatPostedDate(job.postedAt)}
                            </span>
                        )}
                    </div>

                    <Button onClick={scrollToForm} className="mt-6 gap-2" size="lg">
                        Apply for this role
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-4 pb-20 sm:px-6">
                {/* ── Job description ────────────────────────────────────── */}
                <section className="py-10">
                    <h2 className="mb-5 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                        About this role
                    </h2>
                    <JobDescription text={job.jobDescription} />
                </section>

                {/* ── About the company ──────────────────────────────────── */}
                {hasAboutSection && (
                    <section className="border-t border-border py-10">
                        <h2 className="mb-5 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                            About {c.name}
                        </h2>

                        {c.about && (
                            <div className="space-y-3">
                                {c.about.split('\n').filter(l => l.trim()).map((line, i) => (
                                    <p key={i} className="text-[15px] leading-relaxed text-muted-foreground">{line}</p>
                                ))}
                            </div>
                        )}

                        {companyFacts.length > 0 && (
                            <dl className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {companyFacts.map((f, i) => (
                                    <div key={i} className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3.5 py-2.5">
                                        <f.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <dd className="truncate text-sm text-foreground">{f.label}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}

                        {(c.website || c.linkedIn) && (
                            <div className="mt-5 flex flex-wrap gap-4">
                                {c.website && (
                                    <a href={c.website} target="_blank" rel="noopener noreferrer nofollow"
                                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                                        <Globe className="h-3.5 w-3.5" />Visit website
                                    </a>
                                )}
                                {c.linkedIn && (
                                    <a href={c.linkedIn} target="_blank" rel="noopener noreferrer nofollow"
                                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                                        <Linkedin className="h-3.5 w-3.5" />LinkedIn
                                    </a>
                                )}
                            </div>
                        )}
                    </section>
                )}

                {/* ── Apply form ─────────────────────────────────────────── */}
                <section ref={formRef} className="scroll-mt-6">
                    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold tracking-tight text-foreground">Apply for this role</h2>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Takes under a minute. Fields marked <span className="text-destructive">*</span> are required.
                            </p>
                        </div>

                        {submitError && (
                            <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-3 text-sm text-destructive">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>{submitError}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate className="space-y-5">
                            <div className="space-y-1.5">
                                <Label htmlFor="fullName">Full name <span className="text-destructive">*</span></Label>
                                <Input
                                    id="fullName"
                                    value={form.fullName}
                                    onChange={setField('fullName')}
                                    placeholder="Enter Full Name"
                                    autoComplete="name"
                                    disabled={submitting}
                                    aria-invalid={Boolean(errors.fullName)}
                                    className="h-11"
                                />
                                {errors.fullName && <p className="text-xs font-medium text-destructive">{errors.fullName}</p>}
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={form.email}
                                        onChange={setField('email')}
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                        disabled={submitting}
                                        aria-invalid={Boolean(errors.email)}
                                        className="h-11"
                                    />
                                    {errors.email && <p className="text-xs font-medium text-destructive">{errors.email}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="phone">
                                        Phone <span className="font-normal text-muted-foreground">(optional)</span>
                                    </Label>
                                    <Input
                                        id="phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={setField('phone')}
                                        placeholder="+91 98765 43210"
                                        autoComplete="tel"
                                        disabled={submitting}
                                        className="h-11"
                                    />
                                </div>
                            </div>

                            <ResumeField
                                file={file}
                                onPick={pickFile}
                                onClear={() => setFile(null)}
                                error={errors.file}
                                disabled={submitting}
                            />

                            <SkillsChecklist
                                skills={job.screeningSkills}
                                selected={skills}
                                onToggle={name => setSkills(prev =>
                                    prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
                                )}
                                disabled={submitting}
                            />

                            <Button type="submit" size="lg" disabled={submitting} className="w-full gap-2">
                                {submitting
                                    ? <><Loader2 className="h-4 w-4 animate-spin" />Submitting your application…</>
                                    : <>Submit application<ArrowRight className="h-4 w-4" /></>}
                            </Button>

                            <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
                                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                Your details are shared only with {job.company.name}&rsquo;s hiring team and used
                                solely to review your application.
                            </p>
                        </form>
                    </div>
                </section>
            </main>

            <footer className="border-t border-border py-6">
                <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3" />
                    Powered by EchoHire
                </p>
            </footer>
        </div>
    );
}
